import { MARD_291, hexToRgb } from './palette';
import type { PatternRecognitionDraft, PatternRecognitionOptions, ProcessResult, RecognizedColorGroup } from '../types';

type Rgb = { r: number; g: number; b: number };
type Lab = { l: number; a: number; b: number };

const srgbToLinear = (value: number) => value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);

function rgbToOklab(rgb: Rgb): Lab {
  const r = srgbToLinear(rgb.r / 255);
  const g = srgbToLinear(rgb.g / 255);
  const b = srgbToLinear(rgb.b / 255);
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const lr = Math.cbrt(l);
  const mr = Math.cbrt(m);
  const sr = Math.cbrt(s);
  return {
    l: 0.2104542553 * lr + 0.793617785 * mr - 0.0040720468 * sr,
    a: 1.9779984951 * lr - 2.428592205 * mr + 0.4505937099 * sr,
    b: 0.0259040371 * lr + 0.7827717662 * mr - 0.808675766 * sr
  };
}

const paletteLab = MARD_291.map((color, index) => ({ ...rgbToOklab(hexToRgb(color.hex)), index }));

function labDistance(left: Lab, right: Lab) {
  const dl = (left.l - right.l) * 1.35;
  return Math.sqrt(dl * dl + (left.a - right.a) ** 2 + (left.b - right.b) ** 2);
}

function median(values: number[]) {
  if (!values.length) return 0;
  values.sort((a, b) => a - b);
  const middle = Math.floor(values.length / 2);
  return values.length % 2 ? values[middle] : (values[middle - 1] + values[middle]) / 2;
}

function medianRgb(colors: Rgb[]): Rgb {
  return {
    r: Math.round(median(colors.map((color) => color.r))),
    g: Math.round(median(colors.map((color) => color.g))),
    b: Math.round(median(colors.map((color) => color.b)))
  };
}

function luminance(color: Rgb) {
  return color.r * 0.299 + color.g * 0.587 + color.b * 0.114;
}

function chroma(color: Rgb) {
  return Math.max(color.r, color.g, color.b) - Math.min(color.r, color.g, color.b);
}

function rgbDistance(left: Rgb, right: Rgb) {
  return Math.sqrt((left.r - right.r) ** 2 + (left.g - right.g) ** 2 + (left.b - right.b) ** 2);
}

function pixelAt(data: Uint8ClampedArray, imageWidth: number, x: number, y: number): Rgb {
  const index = (y * imageWidth + x) * 4;
  return { r: data[index], g: data[index + 1], b: data[index + 2] };
}

function patchMedian(data: Uint8ClampedArray, imageWidth: number, imageHeight: number, x: number, y: number, radius: number) {
  const colors: Rgb[] = [];
  for (let py = Math.max(0, y - radius); py <= Math.min(imageHeight - 1, y + radius); py += 1) {
    for (let px = Math.max(0, x - radius); px <= Math.min(imageWidth - 1, x + radius); px += 1) colors.push(pixelAt(data, imageWidth, px, py));
  }
  return medianRgb(colors);
}

function sampleCell(data: Uint8ClampedArray, imageWidth: number, imageHeight: number, left: number, top: number, right: number, bottom: number) {
  const cellWidth = Math.max(1, right - left);
  const cellHeight = Math.max(1, bottom - top);
  const radius = Math.max(0, Math.floor(Math.min(cellWidth, cellHeight) * 0.055));
  const points = [
    [0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75],
    [0.5, 0.23], [0.5, 0.77], [0.23, 0.5], [0.77, 0.5]
  ];
  const colors = points.map(([x, y]) => patchMedian(
    data,
    imageWidth,
    imageHeight,
    Math.max(0, Math.min(imageWidth - 1, Math.round(left + cellWidth * x))),
    Math.max(0, Math.min(imageHeight - 1, Math.round(top + cellHeight * y))),
    radius
  ));
  return medianRgb(colors);
}

function centerInkScore(data: Uint8ClampedArray, imageWidth: number, imageHeight: number, left: number, top: number, right: number, bottom: number, backgroundLuminance: number) {
  const x0 = Math.max(0, Math.round(left + (right - left) * 0.26));
  const x1 = Math.min(imageWidth, Math.round(left + (right - left) * 0.74));
  const y0 = Math.max(0, Math.round(top + (bottom - top) * 0.27));
  const y1 = Math.min(imageHeight, Math.round(top + (bottom - top) * 0.73));
  const threshold = Math.min(155, backgroundLuminance - 42);
  let dark = 0;
  let total = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      if (luminance(pixelAt(data, imageWidth, x, y)) < threshold) dark += 1;
      total += 1;
    }
  }
  return total ? dark / total : 0;
}

async function loadImage(file: File) {
  const bitmap = await createImageBitmap(file);
  const maxSide = 2400;
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('无法创建图纸识别画布');
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return { width, height, data: context.getImageData(0, 0, width, height).data };
}

interface WorkingGroup {
  colors: Rgb[];
  center: Rgb;
  lab: Lab;
  cells: number[];
}

function nearestPalette(color: Rgb) {
  const source = rgbToOklab(color);
  const ranked = paletteLab.map((target) => ({ index: target.index, distance: labDistance(source, target) })).sort((a, b) => a.distance - b.distance);
  const best = ranked[0];
  const second = ranked[1];
  const confidence = Math.max(0.2, Math.min(0.98, 0.58 + (second.distance - best.distance) * 5 - best.distance * 1.4));
  return { paletteIndex: best.index, confidence };
}

function toHex(color: Rgb) {
  return `#${[color.r, color.g, color.b].map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

export async function recognizePatternImage(file: File, options: PatternRecognitionOptions): Promise<PatternRecognitionDraft> {
  const started = performance.now();
  if (!file.type.startsWith('image/')) throw new Error('请选择 JPG、PNG 或 WebP 图纸图片');
  const width = Math.max(16, Math.min(512, Math.floor(options.width)));
  const height = Math.max(16, Math.min(512, Math.floor(options.height)));
  const image = await loadImage(file);
  const x0 = Math.round(image.width * options.bounds.left);
  const y0 = Math.round(image.height * options.bounds.top);
  const x1 = Math.round(image.width * options.bounds.right);
  const y1 = Math.round(image.height * options.bounds.bottom);
  if (x1 - x0 < width || y1 - y0 < height) throw new Error('网格范围太小，请扩大选框或减少行列数');

  const samples: Rgb[] = [];
  const inkScores: number[] = [];
  for (let row = 0; row < height; row += 1) {
    const top = y0 + (y1 - y0) * row / height;
    const bottom = y0 + (y1 - y0) * (row + 1) / height;
    for (let col = 0; col < width; col += 1) {
      const left = x0 + (x1 - x0) * col / width;
      const right = x0 + (x1 - x0) * (col + 1) / width;
      const sample = sampleCell(image.data, image.width, image.height, left, top, right, bottom);
      samples.push(sample);
      inkScores.push(centerInkScore(image.data, image.width, image.height, left, top, right, bottom, luminance(sample)));
    }
  }

  const paleSamples = samples.filter((sample) => luminance(sample) > 218 && chroma(sample) < 26);
  const background = paleSamples.length ? medianRgb(paleSamples) : { r: 248, g: 248, b: 248 };
  const empty = new Uint8Array(samples.length);
  const occupiedColors: { cell: number; color: Rgb }[] = [];
  samples.forEach((sample, cell) => {
    const light = luminance(sample);
    const saturation = chroma(sample);
    const distance = rgbDistance(sample, background);
    const colored = saturation >= 18 && distance >= 17;
    const darkFill = light < 80;
    const paleFill = distance >= 20 && light < 245 && saturation >= 10;
    const printedCode = inkScores[cell] >= (Math.min((x1 - x0) / width, (y1 - y0) / height) < 12 ? 0.055 : 0.026);
    const occupied = colored || darkFill || paleFill || printedCode;
    empty[cell] = occupied ? 0 : 1;
    if (occupied) occupiedColors.push({ cell, color: sample });
  });

  const groups: WorkingGroup[] = [];
  const cellGroup = new Uint16Array(samples.length);
  cellGroup.fill(65535);
  const mergeThreshold = Math.min((x1 - x0) / width, (y1 - y0) / height) < 12 ? 0.055 : 0.043;
  occupiedColors.sort((a, b) => luminance(a.color) - luminance(b.color));
  for (const item of occupiedColors) {
    const sourceLab = rgbToOklab(item.color);
    let best = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    groups.forEach((group, index) => {
      const distance = labDistance(sourceLab, group.lab);
      if (distance < mergeThreshold && distance < bestDistance) {
        best = index;
        bestDistance = distance;
      }
    });
    if (best < 0) {
      groups.push({ colors: [item.color], center: item.color, lab: sourceLab, cells: [item.cell] });
      cellGroup[item.cell] = groups.length - 1;
    } else {
      const group = groups[best];
      group.colors.push(item.color);
      group.cells.push(item.cell);
      group.center = medianRgb(group.colors);
      group.lab = rgbToOklab(group.center);
      cellGroup[item.cell] = best;
    }
  }

  const ordered = groups.map((group, oldIndex) => ({ group, oldIndex })).sort((a, b) => b.group.cells.length - a.group.cells.length);
  const remap = new Map<number, number>();
  const recognizedGroups: RecognizedColorGroup[] = ordered.map(({ group, oldIndex }, id) => {
    remap.set(oldIndex, id);
    return { id, sampleHex: toHex(group.center), count: group.cells.length, ...nearestPalette(group.center) };
  });
  for (let cell = 0; cell < cellGroup.length; cell += 1) {
    if (empty[cell]) continue;
    const mapped = remap.get(cellGroup[cell]);
    if (mapped === undefined) {
      empty[cell] = 1;
      cellGroup[cell] = 65535;
    } else cellGroup[cell] = mapped;
  }

  return { width, height, groupIndices: cellGroup, empty, groups: recognizedGroups, elapsedMs: Math.round(performance.now() - started) };
}

export function maskRecognitionCells(draft: PatternRecognitionDraft, cells: Iterable<number>): PatternRecognitionDraft {
  const masked = new Set(cells);
  if (!masked.size) return draft;

  const empty = draft.empty.slice();
  const groupIndices = draft.groupIndices.slice();
  for (const cell of masked) {
    if (cell < 0 || cell >= empty.length) continue;
    empty[cell] = 1;
    groupIndices[cell] = 65535;
  }

  const counts = new Map<number, number>();
  for (let cell = 0; cell < groupIndices.length; cell += 1) {
    if (empty[cell] || groupIndices[cell] === 65535) continue;
    counts.set(groupIndices[cell], (counts.get(groupIndices[cell]) || 0) + 1);
  }

  const remap = new Map<number, number>();
  const groups = draft.groups.flatMap((group) => {
    const count = counts.get(group.id) || 0;
    if (!count) return [];
    const id = remap.size;
    remap.set(group.id, id);
    return [{ ...group, id, count }];
  });

  for (let cell = 0; cell < groupIndices.length; cell += 1) {
    if (empty[cell]) continue;
    const mapped = remap.get(groupIndices[cell]);
    if (mapped === undefined) {
      empty[cell] = 1;
      groupIndices[cell] = 65535;
    } else {
      groupIndices[cell] = mapped;
    }
  }

  return { ...draft, empty, groupIndices, groups };
}

export function materializeRecognition(draft: PatternRecognitionDraft, mappings: Map<number, number>): ProcessResult {
  const colorIndices = new Uint16Array(draft.width * draft.height);
  const empty = draft.empty.slice();
  for (let index = 0; index < colorIndices.length; index += 1) {
    if (empty[index]) continue;
    const paletteIndex = mappings.get(draft.groupIndices[index]);
    if (paletteIndex === undefined || paletteIndex < 0 || !MARD_291[paletteIndex]) {
      empty[index] = 1;
      continue;
    }
    colorIndices[index] = paletteIndex;
  }
  return { width: draft.width, height: draft.height, colorIndices, empty, elapsedMs: draft.elapsedMs };
}

export function matchRecognitionGroupsToPalette(draft: PatternRecognitionDraft, allowedIndices: number[]) {
  const mappings = new Map<number, number>();
  const neutralDark = allowedIndices.filter((index) => {
    const color = hexToRgb(MARD_291[index].hex);
    return chroma(color) < 20 && luminance(color) < 180;
  });
  for (const group of draft.groups) {
    const sourceRgb = hexToRgb(group.sampleHex);
    if (neutralDark.length === 1 && chroma(sourceRgb) < 12 && luminance(sourceRgb) < 190) {
      mappings.set(group.id, neutralDark[0]);
      continue;
    }
    const source = rgbToOklab(sourceRgb);
    const best = allowedIndices.map((index) => {
      const target = paletteLab[index];
      const dl = (source.l - target.l) * 0.35;
      const da = (source.a - target.a) * 2.4;
      const db = (source.b - target.b) * 2.4;
      return { index, distance: dl * dl + da * da + db * db };
    }).sort((a, b) => a.distance - b.distance)[0];
    if (best) mappings.set(group.id, best.index);
  }
  return mappings;
}
